    /**
     * Gift Card Voucher Details Component
     * Fetches enriched voucher data from backend and displays product, booking, and greeting information
     */

    class GiftCardVoucher extends HTMLElement {
    constructor() {
        super();
        this.voucherCode = null;
        this.voucherData = null;
        this.isLoading = false;
        const proxyPath = typeof window.proxyPath === 'string' ? window.proxyPath.trim() : '';
        this.apiEndpoint = proxyPath ? `${proxyPath.replace(/\/$/, '')}/get-voucher-by-code` : null;
    }

    connectedCallback() {
        // Get voucher code from attribute, or fallback to URL parameter
        this.voucherCode = this.getAttribute('voucher-code');
        
        if (!this.voucherCode) {
            // Fallback: Try to get code from URL parameter ?code=
            const urlParams = new URLSearchParams(window.location.search);
            this.voucherCode = urlParams.get('code');
        }

        if (!this.voucherCode || !this.canFetchVoucher()) {
            // Don't bother loading/showing template if basic dependencies are missing
            return;
        }

        // Don't fetch if already loading or data already exists
        if (this.isLoading || this.voucherData) {
            return;
        }

        // Cache DOM element references
        this.cacheElements();

        // Update the code display with formatted code
        this.updateVoucherCodeDisplay();

        this.fetchVoucherData();
    }

    /**
     * Cache frequently accessed DOM elements for better performance
     */
    cacheElements() {
        this.elements = {
            loader: this.querySelector('#voucher-loader'),
            pageOverlay: document.querySelector('.pageOverlay'),
            productTitle: this.querySelector('#voucher-product-title'),
            productDescription: this.querySelector('#voucher-product-description'),
            detailsSection: this.querySelector('#voucher-details'),
            voucherCodeDisplay: this.querySelector('#voucher-code-display'),
            validUntil: this.querySelector('#voucher-valid-until'),
            participants: this.querySelector('#voucher-participants'),
            participantsItem: this.querySelector('#voucher-participants-item'),
            valueItem: this.querySelector('#voucher-value-item'),
            bookingHeadingDefault: this.querySelector('#voucher-booking-heading-default'),
            bookingHeadingExperience: this.querySelector('#voucher-booking-heading-experience'),
            bookingSection: this.querySelector('#voucher-booking-info'),
            bookingInstructionsDefault: this.querySelector('#booking-instructions-default'),
            bookingInstructionsExperience: this.querySelector('#booking-instructions-experience'),
            bookingInstructionsExperienceIntro: this.querySelector('#booking-instructions-experience-intro'),
            registerVoucherLabel: this.querySelector('#register-voucher-label'),
            bookingSteps: this.querySelector('#booking-steps'),
            qrCodeSection: this.querySelector('#voucher-qr-code'),
            codeLabelDefault: this.querySelector('#code-label-default'),
            codeLabelExperience: this.querySelector('#code-label-experience'),
            multiVoucherInfo: this.querySelector('#multi-voucher-info'),
            greetingSection: this.querySelector('#voucher-greeting'),
            greetingTextWrapper: this.querySelector('#greeting-text-wrapper'),
            greetingText: this.querySelector('#greeting-text'),
            // Only cache the first image - images 2, 3, 4 are static from settings
            productImage: this.querySelector('#greeting-image-1'),
            productImageWrapper: this.querySelector('#greeting-image-1')?.parentElement
        };
    }

    /**
     * Update the voucher code display with formatted code
     */
    updateVoucherCodeDisplay() {
        if (!this.elements.voucherCodeDisplay || !this.voucherCode) return;
        
        // Format the code for display (add dashes every 4 characters for long codes)
        const cleanCode = this.voucherCode.replace(/[\s-]/g, '');
        let formattedCode = cleanCode;
        
        if (cleanCode.length >= 12) {
            formattedCode = cleanCode.match(/.{1,4}/g).join('-');
        }
        
        this.elements.voucherCodeDisplay.textContent = formattedCode;
    }

    getTranslation(key, fallback) {
        if (window.TranslationUtils && typeof window.TranslationUtils.get === 'function') {
        return window.TranslationUtils.get(key, fallback);
        }

        return fallback;
    }

    canFetchVoucher() {
        if (!this.voucherCode) return false;
        if (!this.apiEndpoint) return false;
        if (!window.apiService || typeof window.apiService.post !== 'function') return false;
        return true;
    }

    /**
     * Fetch enriched voucher data from the backend API
     */
    async fetchVoucherData() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showLoadingState();

        try {
        // Remove formatting from code (spaces, dashes)
        const cleanCode = this.voucherCode.replace(/[\s-]/g, '');

        const response = await window.apiService.post(this.apiEndpoint, {
            code: cleanCode
        });

        if (response && response.success && response.voucher) {
            this.voucherData = response.voucher;
            const isExperienceVoucher = this.isExperienceVoucher(response.voucher);

            if (isExperienceVoucher) {
            this.showExperienceTemplate();
            } else {
            this.showDefaultTemplate('no-experience-data');
            }

            this.renderVoucherDetails();
        } else {
            this.showDefaultTemplate('empty-response');
        }
        } catch (error) {
        this.handleFetchError(error);
        } finally {
        this.isLoading = false;
        }
    }

    /**
     * Render voucher details to the page
     */
    renderVoucherDetails() {
        if (!this.voucherData) return;

        const voucher = this.voucherData;

        // Update product title if available
        this.updateProductTitle(voucher);

        // Update product description if available
        this.updateProductDescription(voucher);

        // Update voucher details section
        this.updateVoucherDetails(voucher);

        // Update booking information
        this.updateBookingInfo(voucher);

        // Update greeting if exists
        this.updateGreeting(voucher);

        // Update product image if available
        this.updateProductImage(voucher);

        this.hideLoadingState();
        this.dispatchEvent(new CustomEvent('voucher:loaded', { 
        detail: { voucher: this.voucherData },
        bubbles: true 
        }));
    }

    /**
     * Update product title
     */
    updateProductTitle(voucher) {
        if (this.elements.productTitle && voucher.productTitle) {
        this.elements.productTitle.textContent = voucher.productTitle;
        }
    }

    /**
     * Update product description
     */
    updateProductDescription(voucher) {
        if (!this.elements.productDescription) return;

        const isExperience = this.dataset.templateMode === 'experience';
        
        // For experience vouchers: Use giftcardInfo from API if available, otherwise use translation
        if (isExperience) {
            const description = voucher.giftcardInfo;
            if (description && description.trim() !== '') {
                this.elements.productDescription.textContent = description;
                return;
            }
            
            // Fallback to experience translation
            this.elements.productDescription.textContent = this.getTranslation(
                'gift_cards.voucher.default_description',
                'Use this voucher to book your experience. Availability and booking details are available in your account after registration at happy-day.com/book.'
            );
        } else {
            // For default/gift card vouchers: Always use the default card description translation
            this.elements.productDescription.textContent = this.getTranslation(
                'gift_cards.voucher.default_card_description',
                'Experience racing to the fullest. Choose from an AMG GT R, Porsche GT4 RS, Lamborghini Huracan, Porsche GT3 992 Carrera Cup race car, or Ferrari F8 Tributo - and ride along in an Audi RS6 on a racetrack with a professional racing driver.'
            );
        }
    }

    /**
     * Update voucher details section
     */
    updateVoucherDetails(voucher) {
        // Valid until date
        if (this.elements.validUntil) {
        if (voucher.expires_at) {
            const expiryDate = new Date(voucher.expires_at);
            this.elements.validUntil.textContent = this.formatDate(expiryDate);
        }
        }

        // Number of participants with variant information
        if (this.elements.participants) {
        // Show variant title if available, otherwise show participants count, fallback to '-'
        let participantsText = voucher.productVariantTitle || voucher.booking_participants || '-';

        this.elements.participants.textContent = participantsText;
        }
    }

    /**
     * Update booking information section
     */
    updateBookingInfo(voucher) {
        const isExperience = this.dataset.templateMode === 'experience';

        if (isExperience) {
            // Show experience instructions
            if (this.elements.bookingInstructionsDefault) {
                this.elements.bookingInstructionsDefault.setAttribute('hidden', '');
            }
            if (this.elements.bookingInstructionsExperience) {
                this.elements.bookingInstructionsExperience.removeAttribute('hidden');
            }
            if (this.elements.bookingInstructionsExperienceIntro) {
                this.elements.bookingInstructionsExperienceIntro.removeAttribute('hidden');
            }

            // Show QR code for experience vouchers
            if (this.elements.qrCodeSection) {
                this.elements.qrCodeSection.removeAttribute('hidden');
            }
        } else {
            // Show default instructions
            if (this.elements.bookingInstructionsDefault) {
                this.elements.bookingInstructionsDefault.removeAttribute('hidden');
            }
            if (this.elements.bookingInstructionsExperience) {
                this.elements.bookingInstructionsExperience.setAttribute('hidden', '');
            }
            if (this.elements.bookingInstructionsExperienceIntro) {
                this.elements.bookingInstructionsExperienceIntro.setAttribute('hidden', '');
            }

            // Hide QR code for default vouchers
            if (this.elements.qrCodeSection) {
                this.elements.qrCodeSection.setAttribute('hidden', '');
            }
        }

        // Show register voucher label only if it has content (excluding colon)
        if (this.elements.registerVoucherLabel) {
            const labelText = this.elements.registerVoucherLabel.textContent.replace(':', '').trim();
            if (labelText) {
                this.elements.registerVoucherLabel.removeAttribute('hidden');
            }
        }

        // Show booking steps only if it has content
        if (this.elements.bookingSteps) {
            const text = this.elements.bookingSteps.textContent.trim();
            if (text) {
                this.elements.bookingSteps.removeAttribute('hidden');
            }
        }
    }

    /**
     * Update greeting section
     */
    updateGreeting(voucher) {
        // Update greeting text if available from API
        const greeting = voucher.greetingMessage;
        
        if (greeting && greeting.trim() !== '' && this.elements.greetingText && this.elements.greetingTextWrapper) {
        this.elements.greetingText.innerHTML = greeting.replace(/\n/g, '<br>');
        this.elements.greetingTextWrapper.style.display = 'flex';
        }
        // If no greeting from API, wrapper remains hidden
    }

    /**
     * Update product image (only the 1st image - dynamic from product)
     * Images 2, 3, and 4 are static and configured through theme settings
     * Priority: API image first, then default from settings
     */
    updateProductImage(voucher) {
        if (!this.elements.productImage || !this.elements.productImageWrapper) return;
        
        // Handle product image - only update the first image
        // Supports: productImages array, productImageUrls array, or individual URLs
        const images = voucher.productImages || 
                      voucher.productImageUrls || 
                      voucher.images || 
                      [];
        
        let apiImageUrl = null;
        
        // Priority 1: Check for images array
        if (images && images.length > 0) {
            const imageData = images[0];
            // Handle both string URLs and image objects
            apiImageUrl = typeof imageData === 'string' ? imageData : imageData?.url || imageData?.src;
        }
        // Priority 2: Check for single productImageUrl (backward compatibility)
        else if (voucher.productImageUrl) {
            apiImageUrl = voucher.productImageUrl;
        }
        
        // If we have an API image, use it and show the wrapper
        if (apiImageUrl) {
            // Clear srcset and sizes to ensure our src is used
            this.elements.productImage.removeAttribute('srcset');
            this.elements.productImage.removeAttribute('sizes');
            this.elements.productImage.src = apiImageUrl;
            this.elements.productImage.alt = voucher.productTitle ? `${voucher.productTitle} - Image 1` : 'Product image 1';
            this.elements.productImageWrapper.style.display = '';
        } else {
            // No API image available
            const isExperience = this.dataset.templateMode === 'experience';
            
            if (isExperience) {
                // For experience vouchers without API image, hide the wrapper
                this.elements.productImageWrapper.style.display = 'none';
            }
            // For default vouchers, keep the wrapper visible with default image from template
        }
        
        // Note: Images 2, 3, and 4 are now static images set via Liquid template
        // and do not need to be updated dynamically
    }

    /**
     * Format date for display
     */
    formatDate(date) {
        if (!(date instanceof Date) || isNaN(date)) return '-';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    /**
     * Show loading state with page overlay and blur effect
     */
    showLoadingState() {
        // Show page overlay with blur
        if (this.elements.pageOverlay) {
        this.elements.pageOverlay.classList.add('is-visible');
        }
        
        // Show spinning loader
        if (this.elements.loader) {
        this.elements.loader.classList.add('is-visible');
        }
    }

    /**
     * Hide loading state
     */
    hideLoadingState() {
        // Hide page overlay
        if (this.elements.pageOverlay) {
        this.elements.pageOverlay.classList.remove('is-visible');
        }
        
        // Hide spinning loader
        if (this.elements.loader) {
        this.elements.loader.classList.remove('is-visible');
        }
    }

    /**
     * Handle fetch errors with appropriate user feedback
     */
    handleFetchError(error) {
        this.hideLoadingState();

        const status = error && typeof error === 'object' ? error.status : undefined;

        if (status === 404) {
        this.showDefaultTemplate('not-found');
        return;
        }

        // Determine error type and message
        let errorKey = 'fetch_error';
        let fallbackMessage = 'Failed to load voucher details. Please try again.';

        if (status === 401) {
        errorKey = 'unauthorized';
        fallbackMessage = 'You must be logged in to view voucher details.';
        } else if (status === 400) {
        errorKey = 'invalid_code';
        fallbackMessage = 'Invalid gift card code format.';
        } else if (status === 500) {
        errorKey = 'server_error';
        fallbackMessage = 'Server error. Please try again later.';
        }

        // Show error toast notification
        ErrorUtils.error(
        'gift_cards.voucher',
        errorKey,
        fallbackMessage,
        7000
        );

        // Dispatch error event for external handling if needed
        this.dispatchEvent(new CustomEvent('voucher:error', {
        detail: { error, errorKey, message: fallbackMessage },
        bubbles: true
        }));

        this.showDefaultTemplate('fetch-error');
    }
    
    isExperienceVoucher(voucher) {
        if (!voucher || typeof voucher !== 'object') return false;

        if (Object.prototype.hasOwnProperty.call(voucher, 'isExperienceVoucher')) {
        return Boolean(voucher.isExperienceVoucher);
        }

        const typeValue = (voucher.type || voucher.voucherType || '').toString().toLowerCase();
        if (typeValue === 'experience') return true;
        if (typeValue === 'default' || typeValue === 'gift_card') return false;

        return Boolean(voucher.booking_participants || voucher.productVariantTitle);
    }

    showDefaultTemplate(reason = 'fallback') {
        if (this.dataset.templateMode === 'default') {
        this.hideLoadingState();
        return;
        }

        this.dataset.templateMode = 'default';

        if (this.elements.valueItem) {
        this.elements.valueItem.removeAttribute('hidden');
        }

        if (this.elements.participantsItem) {
        this.elements.participantsItem.setAttribute('hidden', '');
        }

        // Update code label
        if (this.elements.codeLabelDefault) {
        this.elements.codeLabelDefault.removeAttribute('hidden');
        }
        if (this.elements.codeLabelExperience) {
        this.elements.codeLabelExperience.setAttribute('hidden', '');
        }

        if (this.elements.bookingHeadingDefault) {
        this.elements.bookingHeadingDefault.removeAttribute('hidden');
        }

        if (this.elements.bookingHeadingExperience) {
        this.elements.bookingHeadingExperience.setAttribute('hidden', '');
        }

        this.hideLoadingState();

        this.dispatchEvent(new CustomEvent('voucher:default', {
        detail: { reason },
        bubbles: true
        }));
    }

    showExperienceTemplate() {
        if (this.dataset.templateMode === 'experience') return;

        this.dataset.templateMode = 'experience';

        if (this.elements.valueItem) {
        this.elements.valueItem.setAttribute('hidden', '');
        }

        if (this.elements.participantsItem) {
        this.elements.participantsItem.removeAttribute('hidden');
        }

        // Update code label
        if (this.elements.codeLabelDefault) {
        this.elements.codeLabelDefault.setAttribute('hidden', '');
        }
        if (this.elements.codeLabelExperience) {
        this.elements.codeLabelExperience.removeAttribute('hidden');
        }

        if (this.elements.bookingHeadingDefault) {
        this.elements.bookingHeadingDefault.setAttribute('hidden', '');
        }

        if (this.elements.bookingHeadingExperience) {
        this.elements.bookingHeadingExperience.removeAttribute('hidden');
        }
    }
    }

    // Register custom element
    if (!customElements.get('gift-card-voucher')) {
    customElements.define('gift-card-voucher', GiftCardVoucher);
    }

